import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { cssVars } from './themeUtils';

const MODE_KEY = 'aptcrm.theme';             // 'light' | 'dark'
const APPEARANCE_KEY = 'aptcrm.appearance';  // the applied theme snapshot: { id?, name, light, dark } or absent = built-in look

const ThemeContext = createContext(null);

function readMode() {
  try {
    const saved = localStorage.getItem(MODE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch (e) { /* storage unavailable */ }
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function readAppearance() {
  try {
    const a = JSON.parse(localStorage.getItem(APPEARANCE_KEY));
    return a && a.light && a.dark ? a : null;
  } catch (e) {
    return null;
  }
}

const applied = new Set(); // inline properties currently set on <html>, so a theme switch clears the old ones

function paint(appearance, mode) {
  const style = document.documentElement.style;
  applied.forEach((k) => style.removeProperty(k));
  applied.clear();
  if (!appearance) return;
  Object.entries(cssVars(appearance[mode], mode)).forEach(([k, v]) => {
    style.setProperty(k, v);
    applied.add(k);
  });
}

/**
 * Holds light/dark mode and the applied theme. The applied theme is a full snapshot kept in
 * localStorage, so it is restored instantly and survives the shared theme being edited or deleted.
 */
export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(readMode);
  const [appearance, setAppearanceState] = useState(readAppearance);

  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    try { localStorage.setItem(MODE_KEY, mode); } catch (e) { /* ignore */ }
    paint(appearance, mode);
  }, [mode, appearance]);

  const toggle = useCallback(() => setMode((m) => (m === 'dark' ? 'light' : 'dark')), []);

  /** Apply `{ id?, name, light, dark }`, or null to go back to the built-in look. */
  const setAppearance = useCallback((next) => {
    setAppearanceState(next);
    try {
      if (next) localStorage.setItem(APPEARANCE_KEY, JSON.stringify(next));
      else localStorage.removeItem(APPEARANCE_KEY);
    } catch (e) { /* ignore */ }
  }, []);

  const value = useMemo(
    () => ({ mode, toggle, appearance, setAppearance }),
    [mode, toggle, appearance, setAppearance]
  );
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useAppearance() {
  return useContext(ThemeContext);
}
